/* eslint-disable max-lines */
import React, { Suspense, lazy, useCallback, FC, HTMLAttributes, useRef, useState, useEffect } from 'react';
import { Redirect } from '@okapi/router-react';
import { PdpLegalDisclaimer } from 'features/legal-disclaimer';
import { PimDisclaimer } from 'features/pim-disclaimer/pim-disclaimer';
import { PdpBreadcrumps } from 'features/pdp-breadcrumps';
import { PdpNavigation } from 'features/page-navigation';
import { Context, ContextType } from 'features/page-navigation/components/section-refs-context';
import { Characteristics } from 'features/product-info/components/characteristics';
import { PdpD9nSection } from 'features/product-description/components';
import { PdpProductReviews } from 'features/product-reviews';
import { PdpBottomD9nSectionState } from 'features/product-description/components/pdp-bottom-d9n-section';
import { PdpCollectionGoods, PdpSuggestionsGoods } from 'features/related-products';
import { PdpPageWarningMessage } from 'features/page-warning-message';
import { Layout } from 'fronton-react';
import { useBreakpoints } from 'common/hooks';
import { ProductMediaContentConnected } from 'features/product-info/components/pdp-showcase/components/product-media-content';
import { ProductInfoContentConnected } from 'features/product-info/components/pdp-showcase/components';
import { Manuals } from 'features/manuals';
import { useWindowSize } from '@navi-toolbox/hooks';
import { cx } from 'linaria';
import { useRRPageView } from 'features/retail-rocket/hooks/use-rr-page-view';
import {
  ComplementMovementBlocks,
  productInformationContainer,
  productInfoDesktopPlusStyle,
  pageContent,
  paddingReset,
  descriptionContent,
  navigation,
  orderedContentNotSellable,
  pdpSectionInsideInfoBlockStyle,
  sectionDivider,
  pdpD9nSectionContainer,
} from './styles';
import { moveContent } from './utils/move-content';
import { PageContentMappedState } from './types';
import { useSelector } from 'react-redux';
import { getBreadcrumbs, getEnableCertificates, getUniqueOffer } from 'features/page-model';
import { getLinkedPagesLevel5 } from 'features/linked-pages/ducks/selectors';
import { LinkedPages } from 'features/linked-pages';
import { OtherProducts } from 'features/other-products';
import { isProductSet } from 'features/product-info/ducks/selectors';
import { UniqueOffer } from 'features/unique-offer/unique-offer';
import { FAQ } from 'features/faq/faq';
import { ProductQualityDocuments } from 'features/product-quality-documents/product-quality-documents';

const LazySubstitutesProducts = lazy(() => import('features/related-products/components/substitute-goods'));
const LazyPdpComplementGoods = lazy(() => import('features/related-products/components/complement-goods'));

const DATA_QA_PDP_SUBSTITUTE_GOODS = 'pdp-substitute-goods';

const descriptionRef = React.createRef<HTMLElement | null>();
const characteristicsRef = React.createRef<HTMLElement | null>();
const collectionGoods = React.createRef<HTMLElement | null>();
const reviewsRef = React.createRef<HTMLElement | null>();
const substituteGoods = React.createRef<HTMLElement | null>();
const complementGoods = React.createRef<HTMLElement | null>();

const refs: ContextType = new Map([
  ['description', descriptionRef],
  ['characteristics', characteristicsRef],
  ['collectionProducts', collectionGoods],
  ['complements', complementGoods],
  ['substitutes', substituteGoods],
  ['reviews', reviewsRef],
]);

type PageContentProps = PageContentMappedState & HTMLAttributes<HTMLElement>;

export const PageContent: FC<PageContentProps> = ({
  seoProductDescription,
  productId,
  quantity,
  isAvailableForPurchase,
  regionId,
  isRedirectTo404,
  ...htmlProps
}) => {
  const movementRef = useRef<HTMLDivElement>(null);
  const [complementBlockIsUp, setComplementBlockIsUp] = useState<boolean>(false);
  const [complementsRefReady, setComplementsRefReady] = useState(false);
  const isSet = useSelector(isProductSet);
  const enableCertificates = useSelector(getEnableCertificates);
  const { width } = useWindowSize();
  const complementsRef = refs.get('complements');

  const handleComplementsRefReady = useCallback(() => {
    setComplementsRefReady(true);
  }, []);

  const updatePosition = useCallback(() => {
    if (isAvailableForPurchase && complementsRefReady) {
      moveContent({
        movementRef,
        complementsRef,
        newQuantity: quantity,
        setComplementBlockIsUp,
      });
    }
  }, [isAvailableForPurchase, quantity, complementsRefReady, complementsRef]);

  const updatePositionRef = useRef(updatePosition);
  useEffect(() => {
    updatePositionRef.current = updatePosition;
  }, [updatePosition]);

  const onCollapsedToggle = useCallback(() => {
    updatePositionRef.current();
  }, []);

  const toggleAnimationMoveContent = useCallback(() => {
    const element = complementsRef?.current;
    if (!element) return;

    const removeAnimation = (e: TransitionEvent) => {
      if (e.propertyName === 'transform') {
        element.style.setProperty('transition', 'none', 'important');
      }
    };

    element.style.setProperty('transition', 'top 300ms ease-in-out', 'important');
    element.addEventListener('transitionend', removeAnimation, { once: true });
  }, [complementsRef]);

  useEffect(() => {
    const shouldUp = !complementBlockIsUp && quantity > 0;
    const shouldDown = complementBlockIsUp && quantity === 0;

    if (shouldUp || shouldDown) {
      toggleAnimationMoveContent();
      updatePositionRef.current();
    }
  }, [complementBlockIsUp, quantity, toggleAnimationMoveContent]);

  useEffect(() => {
    if (complementsRefReady) {
      updatePositionRef.current();
    }
  }, [complementsRefReady, width]);

  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: false,
    largeDesktop: false,
  };

  const shouldDivideProductInfoContent = desktop || largeDesktop;

  useRRPageView(productId, regionId);

  const breadcrumbs = useSelector(getBreadcrumbs) || [];
  const linkedPages = useSelector(getLinkedPagesLevel5);

  const uniqueOfferData = useSelector(getUniqueOffer);

  if (isRedirectTo404) {
    return <Redirect to="/404" />;
  }

  return (
    <>
      <Layout>
        <PdpBreadcrumps className={paddingReset} data={breadcrumbs} />
        <PdpPageWarningMessage />
        <div className={productInformationContainer}>
          <div className={pageContent} data-qa="content-container" {...htmlProps}>
            <ProductMediaContentConnected />
            {!shouldDivideProductInfoContent && <ProductInfoContentConnected />}
            <Context.Provider value={refs}>
              <PdpNavigation className={navigation} />
              <div className={isAvailableForPurchase ? descriptionContent : orderedContentNotSellable}>
                <ComplementMovementBlocks id="description-container" ref={movementRef}>
                  <PdpD9nSection
                    onCollapsedToggle={onCollapsedToggle}
                    className={cx(pdpD9nSectionContainer, complementBlockIsUp ? sectionDivider : undefined)}
                  />
                  <Characteristics
                    className={cx(pdpSectionInsideInfoBlockStyle, sectionDivider)}
                    onCollapsedToggle={onCollapsedToggle}
                  />
                  <PimDisclaimer className={cx(pdpSectionInsideInfoBlockStyle, 'pimDisclaimerWrapper')} />
                  <PdpLegalDisclaimer className={pdpSectionInsideInfoBlockStyle} />

                  {enableCertificates ? <ProductQualityDocuments /> : <Manuals />}

                  {isSet ? (
                    <></>
                  ) : (
                    <>
                      <OtherProducts className={pdpSectionInsideInfoBlockStyle} />

                      {uniqueOfferData?.enable && <UniqueOffer data={uniqueOfferData} />}

                      {isAvailableForPurchase && (
                        <PdpCollectionGoods
                          className={pdpSectionInsideInfoBlockStyle}
                          id="collectionProducts"
                        />
                      )}

                      <PdpSuggestionsGoods productId={productId} />

                      {!isAvailableForPurchase && (
                        <PdpCollectionGoods
                          className={pdpSectionInsideInfoBlockStyle}
                          id="collectionProducts"
                        />
                      )}
                    </>
                  )}
                </ComplementMovementBlocks>
                {!isSet && (
                  <>
                    <div>
                      <Suspense fallback="...Загрузка">
                        <LazyPdpComplementGoods
                          className={pdpSectionInsideInfoBlockStyle}
                          productId={productId}
                          onRefReady={handleComplementsRefReady}
                        />
                      </Suspense>
                      <Suspense fallback="...Загрузка">
                        <LazySubstitutesProducts
                          productId={productId}
                          data-qa={DATA_QA_PDP_SUBSTITUTE_GOODS}
                          id="substitutes"
                        />
                      </Suspense>
                    </div>
                    <PdpProductReviews id="reviews" className={sectionDivider} />
                  </>
                )}
              </div>
            </Context.Provider>
          </div>
          {shouldDivideProductInfoContent && (
            <div>
              <ProductInfoContentConnected className={productInfoDesktopPlusStyle} />
            </div>
          )}
        </div>

        {!isSet && linkedPages && linkedPages.length > 0 && <LinkedPages pages={linkedPages} />}
        <FAQ />
      </Layout>
      <PdpBottomD9nSectionState />
    </>
  );
};
